import React, { useEffect, useContext, useReducer, useState } from 'react';
import reducer from '../reducers/cart_reducer';
import {
  ADD_TO_CART,
  REMOVE_CART_ITEM,
  TOGGLE_CART_ITEM_AMOUNT,
  CLEAR_CART,
  COUNT_CART_TOTALS,
  SET_DETAILED_CART,
  APPLY_COUPON,
} from '../actions';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useUserContext } from './user_context';

const initialState = {
  cart: [],
  total_items: 0,
  total_amount: 0,
  shipping_fee: 0,
  mainColor: '',
  code: '',
  couponDiscount: 0,
  total_tax: 0,
};

const CartContext = React.createContext();

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { currentUser } = useUserContext();
  const [isLoaded, setIsLoaded] = useState(false);
  const [detailedCart, setDetailedCart] = useState([]);

  const setMainColor = (color) => {
    dispatch({ type: 'SET_MAIN_COLOR', payload: color });
  };

  const fetchCartFromBackend = async (userid) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/${userid}`
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching cart from backend:', error);
      return [];
    }
  };

  const initializeCart = async () => {
    if (currentUser?.id) {
      let backendCart = [];
      backendCart = await fetchCartFromBackend(currentUser.id);

      const detailedCartItems = await Promise?.all(
        backendCart?.map(async (item) => {
          const productDetails = await axios.get(
            `${process.env.REACT_APP_BACKEND_HOST}/api/products/${item.id}`
          );

          if (productDetails) {
            return {
              id: item?.id,
              name: productDetails?.data?.data?.name,
              code: productDetails?.data?.data?.productCode,
              amount: item?.quantity,
              image: productDetails?.data?.data?.productImageUrl,
              price: productDetails?.data?.data?.price,
              shipping: productDetails?.data?.data?.shipping,
              taxPrice: productDetails?.data?.data?.taxPrice,
              totalProductPrice: productDetails?.data?.data?.price,
              description: productDetails?.data?.data?.description,
              discountPercentage:
                productDetails?.data?.data?.discountPercentage,
              weight: productDetails?.data?.data?.weight,
              makingPercentage: productDetails?.data?.data?.makingPercentage,
              metalType: productDetails?.data?.data?.metalType,
              subPrice: productDetails?.data?.data?.subPrice,
              totalMetalPrice: productDetails?.data?.data?.subPrice,
              makingPrice: productDetails?.data?.data?.makingPrice,
              max: productDetails?.data?.data?.quantity,
              category: productDetails?.data?.data?.category
            };
          } else {
            return null;
          }
        })
      );
      const validCartItems = detailedCartItems.filter((item) => item !== null);
      setDetailedCart(validCartItems);
      dispatch({ type: SET_DETAILED_CART, payload: validCartItems });
    }
    setIsLoaded(true);
  };

  const handlecartButton = async (
    id,
    amount,
    currentUser,
    mainSize,
    setCartButtonText,
    onOpen,
    code,
    bestOffers,
    taxPrice,
    productWeight,
    discountPrice,
    productPrice,
    productMrp,
    quantity
  ) => {
    let userid = currentUser?.id;
    if (currentUser) {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/${userid}`
      );
      const array = await response?.data?.data;

      const isPresent = array?.some((order) => order.id === id);

      const data = {
        order_id: id,
        quantity: amount,
        size: mainSize,
        code: code,
        bestOffers: bestOffers,
        taxPrice: taxPrice,
        productWeight: productWeight,
        productMrp: productMrp,
        productPrice: productPrice,
        productDiscount: discountPrice,
      };
      const options = {
        id: id,
        quantity: amount,
      };
      if (!isPresent) {
        if(quantity<1){
         return  toast.error("Product not available")
        }
        try {
          await axios.put(
            `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/${userid}`,
            options
          );
          toast.success('Item added to cart');
          // setCartButtonText('Remove from cart');
          await initializeCart();
        } catch (error) {
          console.error('Error adding item to cart:', error);
        }
      } else if (isPresent) {
        try {
          const data = {
            id: id,
          };
          await axios.delete(
            `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/${userid}`,
            { data }
          );
          toast.success('Item removed from cart');
          // setCartButtonText('Add to cart');
          await initializeCart();
        } catch (error) {
          console.error('Error deleting item from cart:', error);
        }
      }
    } else {
      onOpen();
    }
  };

  useEffect(() => {
    initializeCart();
  }, [currentUser]);

  const addToCart = (id, amount, product) => {
    dispatch({
      type: ADD_TO_CART,
      payload: { id, amount, product },
    });
    toast.success('Item added to cart');
  };

  const removeItem = async (id) => {
    const userid = currentUser?.id;
    const data = {
      id: id,
    };
    await axios.delete(
      `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/${userid}`,
      { data }
    );
    await initializeCart();
    dispatch({ type: REMOVE_CART_ITEM, payload: id });
  };

  const toggleAmount = (id, value) => {
    dispatch({ type: TOGGLE_CART_ITEM_AMOUNT, payload: { id, value } });
  };

  const clearCart = async () => {
    const userid = currentUser?.id;
    await axios.delete(
      `${process.env.REACT_APP_BACKEND_HOST}/api/user/addtocart/clearcart/${userid}`
    );
    await initializeCart();
    dispatch({ type: CLEAR_CART });
  };

  useEffect(() => {
    if (isLoaded) {
      dispatch({ type: COUNT_CART_TOTALS });
      localStorage.setItem('cart', JSON.stringify(state.cart));
    }
  }, [state.cart, isLoaded]);

  const applyCoupon = (discount) => {
    dispatch({
      type: APPLY_COUPON,
      payload: discount,
    });
  };

  return (
    <CartContext.Provider
      value={{
        ...state,
        addToCart,
        removeItem,
        toggleAmount,
        clearCart,
        detailedCart,
        handlecartButton,
        setMainColor,
        applyCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  return useContext(CartContext);
};
